<template>
  <div class="social-share-container btn cfa-btn2-primary mx-2" :class="{ 'disabled': disabled }">
    <button 
      class="social-share-btn"
      @click="toggleMenu"
      :class="{ 'active': isMenuOpen }"
      :disabled="disabled"
    >
      <i class="fa fa-share-alt"></i>
      <span class="btn-text">{{ $t('share') }}</span>
    </button>

    <div 
      v-if="isMenuOpen" 
      class="social-share-menu"
      :class="{ 'show': isMenuOpen }"
    >
      <div class="social-share-header">
        <h6>Partager sur les réseaux sociaux</h6>
        <button class="close-btn" @click="closeMenu">
          <i class="fa fa-times"></i>
        </button>
      </div>
      
      <div v-if="loading" class="social-share-loading p-4 text-center">
        <div class="spinner-border text-primary spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="small text-muted mt-2">{{ $t('loading') || 'Chargement...' }}</div>
      </div>
      
      <div v-else class="social-networks">
        <button 
          class="social-btn facebook"
          @click="shareOnFacebook"
          :title="$t('share_on_facebook')"
        >
          <i class="fa-brands fa-facebook-f"></i>
          <span>Facebook</span>
        </button>

        <button 
          class="social-btn twitter"
          @click="shareOnTwitter"
          :title="$t('share_on_twitter')"
        >
          <i class="fa-brands fa-twitter"></i>
          <span>Twitter</span>
        </button>

        <!-- LinkedIn -->
        <button 
          class="social-btn linkedin"
          @click="shareOnLinkedIn"
          :title="$t('share_on_linkedin')"
        >
          <i class="fa-brands fa-linkedin-in"></i>
          <span>LinkedIn</span>
        </button>
        <!-- Email -->
        <button 
          class="social-btn email"
          @click="shareByEmail"
          :title="$t('share_by_email')"
        >
          <i class="fa fa-envelope"></i>
          <span>{{ $t('email') }}</span>
        </button>
      </div>
    </div>

    <!-- Overlay pour fermer le menu -->
    <div 
      v-if="isMenuOpen" 
      class="social-share-overlay"
      @click="closeMenu"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'SocialShare',
  props: {
    url: {
      type: String,
      default: () => window.location.href
    },
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    hashtags: {
      type: String,
      default: ''
    },
    shareUrls: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isMenuOpen: false,
    }
  },
  computed: {
    shareUrl() {
      return encodeURIComponent(this.url)
    },
    shareTitle() {
      return encodeURIComponent(this.title)
    },
    shareDescription() {
      return encodeURIComponent(this.description)
    },
    shareHashtags() {
      return encodeURIComponent(this.hashtags)
    }
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
      if (this.isMenuOpen) {
        this.$emit('share-url')
      }
    },
    
    closeMenu() {
      this.isMenuOpen = false
    },

    shareOnFacebook() {
      if (this.shareUrls && this.shareUrls.facebook) {
         this.openShareWindow(this.shareUrls.facebook)
      } else {
         let facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${this.shareUrl}`
         this.openShareWindow(facebookUrl)
      }
      this.closeMenu()
    },

    // Partage sur Twitter
    shareOnTwitter() {
      if (this.shareUrls && this.shareUrls.twitter) {
         this.openShareWindow(this.shareUrls.twitter)
      } else {
        let twitterText = ''
        if (this.title) {
          twitterText += this.title + ' '
        }
        if (this.description) {
          twitterText += this.description + ' '
        }
        if (this.hashtags) {
          twitterText += this.hashtags + ' '
        }
        
        let twitterUrl = `https://twitter.com/intent/tweet?url=${this.shareUrl}`
        if (twitterText.trim()) {
          twitterUrl += `&text=${encodeURIComponent(twitterText.trim())}`
        }
        this.openShareWindow(twitterUrl)
      }
      this.closeMenu()
    },

    // Partage sur LinkedIn
    shareOnLinkedIn() {
      if (this.shareUrls && this.shareUrls.linkedin) {
         this.openShareWindow(this.shareUrls.linkedin)
      } else {
         let linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${this.shareUrl}`
         this.openShareWindow(linkedinUrl)
      }
      this.closeMenu()
    },

    // Partage par email
    shareByEmail() {
      if (this.shareUrls && this.shareUrls.email) {
         window.location.href = this.shareUrls.email
      } else {
        let emailSubject = this.title || 'Partage d\'un lien'
        let emailBody = ''
        
        if (this.description) {
          emailBody += this.description + '\n\n'
        }
        emailBody += this.url

        const emailUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`
        window.location.href = emailUrl
      }
      this.closeMenu()
    },


    // Ouvrir une fenêtre de partage
    openShareWindow(url) {
      const width = 600
      const height = 400
      const left = (window.innerWidth - width) / 2
      const top = (window.innerHeight - height) / 2
      
      window.open(
        url,
        'share',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      )
    }
  },
  
  mounted() {
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.closeMenu()
      }
    })
  }
}
</script>

<style scoped>
.social-share-container {
  position: relative;
  display: inline-block;
}

.social-share-btn {
  color: var(--primary-color);
  border: none;
  padding: 0;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.social-share-btn i {
  font-size: 16px;
}

.social-share-menu {
  position: absolute;
  top: 42px;
  left: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 280px;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  pointer-events: none;
}

.social-share-menu.show {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.social-share-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.social-share-header h6 {
  margin: 0;
  color: #333;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.social-networks {
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.social-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  text-align: left;
  background: #f8f9fa;
  color: #333;
}

.social-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.social-btn i {
  font-size: 18px;
  width: 20px;
  text-align: center;
}

/* Couleurs spécifiques pour chaque réseau */
.social-btn.facebook:hover {
  background: #1877f2;
  color: white;
}

.social-btn.twitter:hover {
  background: #1da1f2;
  color: white;
}

.social-btn.linkedin:hover {
  background: #0077b5;
  color: white;
}

.social-btn.whatsapp:hover {
  background: #25d366;
  color: white;
}

.social-btn.email:hover {
  background: var(--primary-color);
  color: white;
}

.social-share-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  z-index: 999;
}

.disabled {
  cursor: not-allowed;
  pointer-events: none;
  background-color: #d9d9d9 !important;
  border-color: #d9d9d9 !important;
  color: #fff !important;
  opacity: 1;
  box-shadow: none;
}

.disabled .social-share-btn {
  color: #fff !important;
  pointer-events: none;
}


.disabled:hover {
  transform: none;
  cursor: not-allowed;
  opacity: 0.6;
  pointer-events: none;
}

/* Responsive */
@media (max-width: 768px) {
  .social-networks {
    grid-template-columns: 1fr;
  }
  
  .social-share-menu {
    min-width: 250px;
  }
  
  .social-share-btn .btn-text {
    display: none;
  }
  
  .social-share-btn {
    padding: 12px;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    justify-content: center;
  }
}

/* Animation d'entrée */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.social-share-menu.show {
  animation: slideIn 0.3s ease;
}
</style>